import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { findGateDuration, nextGateMotion, parseStateAt } from './gateMotion'
import { GET, POST } from 'rest/request'
import * as urls from 'rest/urls'
import { RootState } from 'store'

const initialState: warehouse.initialState = {
  status: 'idle',
  warehouses: [],
  moreActions: {},
  selectedGateGateway: {},
  state: {
    refreshDate: Date.now(),
    warehouses: {},
    alarms: {},
    places: {},
  }
}

export const getWarehouses = createAsyncThunk<warehouse.WarehousesResponse, string>('warehouses/getWarehouses', async (userId) => {
  return await GET(urls.warehouses(userId))
})

export const getObjectsState = createAsyncThunk<warehouse.ObjectState, boolean | undefined>('warehouses/getObjectsState', async () => {
  return await POST(urls.getobjectstates)
})

export const warehousesSlice = createSlice({
  name: 'warehouses',
  initialState,
  reducers: {
    updateWarehouse: (state, action: PayloadAction<{ id: string, values: Partial<warehouse.Warehouse> }>) => {
      const { id, values } = action.payload
      if (id && values) {
        state.warehouses = state.warehouses.map(warehouse => warehouse.objectId === id
          ? { ...warehouse, ...values }
          : warehouse)
      }
    },
    updateWarehouseState: (state, action: PayloadAction<{ id: string, doorState: Partial<warehouse.ObjectStateWarehouse> }>) => {
      state.warehouses = state.warehouses.map(warehouse => warehouse.objectId === action.payload.id
        ? { ...warehouse, doorState: { ...warehouse.doorState, ...action.payload.doorState } }
        : warehouse)
      state.state = {
        ...(state.state || { warehouses: {}, places: {}, alarms: {} }),
        warehouses: {
          ...(state.state?.warehouses || {}),
          [action.payload.id]: {
            ...(state.state?.warehouses?.[action.payload.id] || { lock: 'locked', door: 'closed' }),
            ...action.payload.doorState
          }
        }
      }
    },
    updateWarehouseAlarmState: (state, action: PayloadAction<{ id: string, alarmState: Partial<warehouse.ObjectAlarmState> }>) => {
      state.warehouses = state.warehouses.map(warehouse => warehouse.objectId === action.payload.id
        ? { ...warehouse, alarmState: { ...warehouse.alarmState, ...action.payload.alarmState } }
        : warehouse)
      state.state = {
        ...(state.state || { warehouses: {}, places: {}, alarms: {} }),
        alarms: {
          ...(state.state?.alarms || {}),
          [action.payload.id]: {
            ...(state.state?.alarms?.[action.payload.id] || { fire_alarmed: 'no', alarmed: 'no', zone: 'disarmed' }),
            ...action.payload.alarmState
          }
        }
      }
    },
    updateGateState: (state, action: PayloadAction<{
      id: string
      placeId: string
      state: warehouse.GateState
      stateAt?: string | null
      position?: warehouse.GatePosition
    }>) => {
      const now = Date.now()
      const prevGate = state.state?.places?.[action.payload.placeId]?.gates?.[action.payload.id]
      const motion = nextGateMotion(
        prevGate,
        action.payload.state,
        findGateDuration(state.warehouses, action.payload.placeId, action.payload.id, action.payload.state),
        now,
        parseStateAt(action.payload.stateAt, now)
      )
      if (action.payload.state === 'gate_closed') {
        state.moreActions[action.payload.placeId] = false
      }
      state.state = {
        ...(state.state || { warehouses: {}, places: {}, alarms: {} }),
        places: {
          ...(state.state?.places || {}),
          [action.payload.placeId]: {
            ...(state.state?.places?.[action.payload.placeId] || { gates: {}, gateways: {} }),
            gates: {
              ...(state.state?.places?.[action.payload.placeId]?.gates || {}),
              [action.payload.id]: {
                ...(prevGate || { id: action.payload.id, name: '', description: '' }),
                ...motion,
                state: action.payload.state,
                ...(action.payload.stateAt !== undefined ? { stateAt: action.payload.stateAt } : {}),
                ...(action.payload.position ? { position: action.payload.position } : {}),
              }
            }
          }
        }
      }
    },
    updateGatewayState: (state, action: PayloadAction<{ id: string, placeId: string, state: warehouse.GatewayState }>) => {
      state.state = {
        ...(state.state || { warehouses: {}, places: {}, alarms: {} }),
        places: {
          ...(state.state?.places || {}),
          [action.payload.placeId]: {
            ...(state.state?.places?.[action.payload.placeId] || { gates: {}, gateways: {} }),
            gateways: {
              ...(state.state?.places?.[action.payload.placeId]?.gateways || {}),
              [action.payload.id]: {
                ...(state.state?.places?.[action.payload.placeId]?.gateways?.[action.payload.id] || { id: action.payload.id, name: '', description: '' }),
                state: action.payload.state
              }
            }
          }
        }
      }
    },
    updateMoreActions: (state, action: PayloadAction<{ gateId: string, value: boolean }>) => {
      state.moreActions[action.payload.gateId] = action.payload.value
    },
    updateSelectedGateGateway: (state, action: PayloadAction<{ placeId: string, object: warehouse.GateGateway, objectId: string }>) => {
      state.selectedGateGateway = {
        ...state.selectedGateGateway,
        [action.payload.placeId]: {
          ...(state.selectedGateGateway[action.payload.placeId] || {}),
          [action.payload.object]: action.payload.objectId
        }
      }
    },
    updateWarehouseActionKeyCabinetGrant: (state, action: PayloadAction<{ id: string, grant: warehouse.WarehouseActionKeyCabinetGrant }>) => {
      state.warehouses = state.warehouses.map(warehouse => warehouse.objectId === action.payload.id
        ? {
          ...warehouse,
          actions: {
            ...warehouse.actions,
            key_cabinet: {
              ...(warehouse.actions.key_cabinet || {
                id: '',
                name: '',
                placeId: '',
                placeName: '',
                location: '',
                instruction: '',
                isOnline: false
              }),
              grant: action.payload.grant
            }
          }
        }
        : warehouse)
    },
    clearWarehousesList: () => {
      return initialState
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getWarehouses.pending, (state) => {
        state.status = 'pending'
      })
      .addCase(getWarehouses.rejected, (state) => {
        state.status = 'failed'
      })
      .addCase(getWarehouses.fulfilled, (state, action) => {
        if (action.payload.data) {
          state.status = 'succeed'
          state.warehouses = action.payload.data.warehouses
        }
      })
      .addCase(getObjectsState.pending, (state, action) => {
        if (action.meta.arg) {
          state.state = {
            ...(state.state || { warehouses: {}, places: {}, alarms: {} }),
            isLoading: true
          }
        }
      })
      .addCase(getObjectsState.fulfilled, (state, action) => {
        if (action.payload) {
          if (state.warehouses.length > 0 && action.payload.warehouses) {
            state.warehouses = state.warehouses.map(warehouse => warehouse.objectId && (action.payload?.warehouses?.[warehouse.objectId] || action.payload?.alarms?.[warehouse.objectId])
              ? {
                ...warehouse,
                doorState: action.payload?.warehouses?.[warehouse.objectId] || warehouse.doorState,
                alarmState: action.payload?.alarms?.[warehouse.objectId] || warehouse.alarmState,
              } : warehouse)
          }
          const now = Date.now()
          const places = action.payload.places && Object.fromEntries(
            Object.entries(action.payload.places).map(([placeId, place]) => [placeId, {
              ...place,
              gates: place?.gates && Object.fromEntries(
                Object.entries(place.gates).map(([gateId, gate]) => [gateId, gate?.state
                  ? {
                    ...gate,
                    ...nextGateMotion(
                      state.state?.places?.[placeId]?.gates?.[gateId],
                      gate.state,
                      findGateDuration(state.warehouses, placeId, gateId, gate.state),
                      now,
                      parseStateAt(gate.stateAt, now)
                    )
                  }
                  : gate])
              )
            }])
          )
          state.state = {
            ...state.state,
            ...action.payload,
            ...(places ? { places } : {}),
            refreshDate: now,
            isLoading: false,
          }
        }
      })
  },
})

export const { updateWarehouse, updateWarehouseState, updateWarehouseAlarmState, updateGateState, updateGatewayState, updateMoreActions, updateSelectedGateGateway, updateWarehouseActionKeyCabinetGrant, clearWarehousesList } = warehousesSlice.actions

export const selectWarehouseStatus = (state: RootState) => state.warehouses.status
export const selectWarehouses = (state: RootState) => state.warehouses.warehouses
export const selectWarehouseMoreActions = (state: RootState) => state.warehouses.moreActions
export const selectWarehouseSelectedGateGateway = (state: RootState, placeId: string) => state.warehouses.selectedGateGateway[placeId]
export const selectWarehouseIndex = (state: RootState, id: string) => {
  return state.warehouses.warehouses.findIndex(w => w.objectId === id)
}
export const selectWarehouse = (state: RootState, id: string) => {
  return state.warehouses.warehouses.find(w => w.objectId === id) || {} as warehouse.Warehouse
}
export const selectWarehouseAllStates = (state: RootState) => state.warehouses.state
export const selectWarehouseStatesIsLoading = (state: RootState) => state.warehouses.state?.isLoading
export const selectWarehouseStatesRefreshDate = (state: RootState) => state.warehouses.state?.refreshDate
export const selectWarehouseStateWarehouses = (state: RootState, id: string) => state.warehouses.state?.warehouses?.[id]
export const selectWarehouseStatePlace = (state: RootState, placeId: string) => state.warehouses.state?.places?.[placeId]
export const selectWarehouseStateGate = (state: RootState, placeId?: string, gateId?: string) =>
  placeId && gateId ? state.warehouses.state?.places?.[placeId]?.gates?.[gateId] : undefined

export default warehousesSlice.reducer
